import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import type React from 'react';

import type { ExtendedGridColDef } from '../components/common/FilterModal';
import type { BusinessUnitConversationTaggingConfig } from '../services/types/businessUnit';
import type { ExtendedLead } from '../services/types/lead';
import formatClientId from './clientIdFormatter';
import { BRAVOAUTO_BUSINESS_UNIT_ID, INSUREX_BUSINESS_UNIT_ID, KOVACS_BUSINESS_UNIT_ID } from './constants';
import { LEAD_ORIGINS } from './leadOrigins';
import { getTopicColor } from './topicColor';

type TopicConfigList = BusinessUnitConversationTaggingConfig['topics'] | null | undefined;

/**
 * Translates lead status from English to Spanish
 * @param status - The lead status in English
 * @returns The translated status in Spanish
 */
export const translateLeadStatus = (status: string): string => {
  const statusTranslations: Record<string, string> = {
    CREATED: 'Creado',
    HANDLED_BY_AI: 'IA',
    INTERESTING_FOR_SALESMAN: 'Interesante',
    ASSIGNED_TO_SALESMAN: 'Asignado',
    PENDING_TO_BE_HANDLED: 'Pendiente',
    PENDING_TO_BE_ASSIGNED: 'Por asignar',
    HANDLED_BY_SALESMAN: 'Manejando',
    CLOSED: 'Cerrado',
  };

  return statusTranslations[status] || status;
};

/**
 * Reads the license plate out of the raw payload persisted at lead creation. Only leads
 * whose source stores a `patente` in that payload (Destacados) resolve to a value; anything
 * else — a different payload shape, or malformed JSON — yields an empty string.
 */
export const getPatenteFromWebhookBody = (lead: ExtendedLead): string => {
  if (!lead.webhookBodyAtCreation) return '';
  try {
    const parsed: unknown = JSON.parse(lead.webhookBodyAtCreation);
    if (!parsed || typeof parsed !== 'object') return '';
    const { patente } = parsed as { patente?: unknown };
    return typeof patente === 'string' ? patente : '';
  } catch {
    return '';
  }
};

/**
 * Date formatting utility
 */
const last24hoursDate = new Date();
last24hoursDate.setDate(last24hoursDate.getDate() - 1);

const formatDateValue = (value: Date | null) => {
  if (!value) return 'No';
  const dateValue = new Date(value);
  if (dateValue > last24hoursDate) return dateValue.toLocaleTimeString('es-CL');
  return `${dateValue.getDate()}/${dateValue.getMonth() + 1} ${dateValue.getHours()}:${dateValue
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;
};

/**
 * Creates a single select or string column based on available options
 */
const createSingleSelectOrStringColumn = (
  field: string,
  headerName: string,
  flex: number,
  minWidth: number,
  options?: string[] | null,
  renderCell?: (params: { row: ExtendedLead }) => React.ReactNode,
  valueGetter?: ExtendedGridColDef['valueGetter']
): ExtendedGridColDef => {
  if (options && options.length > 0) {
    return {
      field,
      headerName,
      flex,
      minWidth,
      type: 'singleSelect',
      valueOptions: options,
      renderCell,
      valueGetter,
    };
  }
  return { field, headerName, flex, minWidth, type: 'string', renderCell, valueGetter };
};

/**
 * Common columns shared across all business unit types
 */
const renderTopicsCell = (topics: string[], topicsConfig: TopicConfigList) => (
  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap', alignItems: 'center', py: 0.5, overflow: 'hidden' }}>
    {topics.map((topic) => {
      const cfg = topicsConfig?.find((t) => t.name === topic);
      const color = getTopicColor(topic, cfg?.color);
      return (
        <Chip
          key={topic}
          label={topic}
          size="small"
          sx={{
            fontSize: '0.6rem',
            height: 20,
            backgroundColor: `${color}22`,
            color,
            fontWeight: 600,
            fontFamily: 'Jost',
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
            '& .MuiChip-label': { px: 0.75 },
            flexShrink: 0,
          }}
        />
      );
    })}
  </Box>
);

const createCommonColumns = (
  sourceValues: string[],
  campaignValues: string[],
  topicsConfig: TopicConfigList
): ExtendedGridColDef[] => [
  {
    field: 'id',
    headerName: 'ID Lead',
    flex: 0.5,
    minWidth: 80,
  },
  {
    field: 'client.businessUnit.name',
    headerName: 'Unidad Negocio',
    flex: 1,
    minWidth: 140,
    valueGetter: (_, row: ExtendedLead) => (row.client?.businessUnit?.name as string) || '',
    filterable: false,
    sortable: false,
  },
  {
    field: 'client.id',
    headerName: 'Cliente',
    flex: 0.5,
    minWidth: 80,
    valueGetter: (_, row: ExtendedLead) => row.client?.id || 0,
    valueFormatter: formatClientId,
  },
  {
    field: 'client.name',
    headerName: 'Nombre',
    flex: 2,
    minWidth: 200,
    valueGetter: (_, row: ExtendedLead) => `${row.client?.firstName || ''} ${row.client?.lastName || ''}`.trim(),
  },
  {
    field: 'client.phone',
    headerName: 'Teléfono',
    flex: 1,
    minWidth: 130,
    valueGetter: (_, row: ExtendedLead) => row.client?.phone || '',
  },
  {
    field: 'client.rut',
    headerName: 'RUT',
    flex: 1,
    minWidth: 120,
    valueGetter: (_, row: ExtendedLead) => (row.client?.rut as string) || '',
  },
  {
    field: 'client.notes',
    headerName: 'Notas',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (_, row: ExtendedLead) => row.client?.notes ?? '',
    filterable: true,
    sortable: false,
  },
  {
    field: 'topics',
    headerName: 'Tags',
    flex: 3,
    minWidth: 400,
    valueGetter: (_, row: ExtendedLead) => row.topics?.join(', ') || '',
    renderCell: (params: { row: ExtendedLead }) => renderTopicsCell(params.row.topics ?? [], topicsConfig),
    filterable: true,
    sortable: false,
    filterInputType: 'text-multi' as const,
    filterOperatorLabel: 'Contiene',
  },
  createSingleSelectOrStringColumn('source', 'Origen', 1.5, 180, sourceValues),
  createSingleSelectOrStringColumn('origin', 'Categoría', 1, 160, [...LEAD_ORIGINS]),
  {
    field: 'subOrigin',
    headerName: 'Suborigen',
    flex: 1,
    minWidth: 140,
    valueGetter: (_, row: ExtendedLead) => row.subOrigin ?? '',
  },
  {
    field: 'businessUnitAd',
    headerName: 'Campaña',
    flex: 1.5,
    minWidth: 200,
    renderCell: (params: { row: ExtendedLead }) => {
      const campaignSourceName = params.row.businessUnitAd?.campaignSourceName || params.row.businessUnitAd?.adSourceId;
      return (
        <div>
          {params.row.businessUnitAd?.adSource}
          {campaignSourceName && ` | ${campaignSourceName}`}
        </div>
      );
    },
    filterable: false,
    sortable: false,
  },
  {
    field: 'metaCampaignName',
    headerName: 'Campaña Meta',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (_, row: ExtendedLead): string => {
      // Type guard to safely access nested metaCampaign property through metaAdSet
      const metaAd = row.metaAd as { metaAdSet?: { metaCampaign?: { name: string | null } } | null } | undefined;
      return metaAd?.metaAdSet?.metaCampaign?.name || '';
    },
  },
  {
    field: 'metaAdName',
    headerName: 'Nombre de Anuncio',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (_, row: ExtendedLead) => {
      const name = (row.metaAd as { name: string | null } | undefined)?.name;
      return name || '';
    },
  },
  {
    field: 'metaAdId',
    headerName: 'Id de Anuncio',
    flex: 1,
    minWidth: 150,
    valueGetter: (_, row: ExtendedLead) => {
      const id = (row.metaAd as { metaAdId: string | null } | undefined)?.metaAdId;
      return id != null ? String(id) : '';
    },
  },
  {
    field: 'whatsAppCampaign',
    headerName: 'Campaña Outbound',
    flex: 1,
    minWidth: 150,
    valueOptions: campaignValues,
    type: 'singleSelect',
    supportsSingleValueOperators: true,
    renderCell: (params: { row: ExtendedLead }) => {
      /* To handle the old campaigns that use the outboundCampaignId field */
      return <div>{params.row.outboundCampaignId || params.row.whatsAppCampaign?.name}</div>;
    },
  },
  {
    field: 'UTM.campaign',
    headerName: 'Id Campaña',
    flex: 1,
    minWidth: 150,
    valueGetter: (_, row: ExtendedLead) => row.UTM?.campaign || '',
  },
  {
    field: 'UTM.source',
    headerName: 'Fuente',
    flex: 1,
    minWidth: 130,
    valueGetter: (_, row: ExtendedLead) => row.UTM?.source || '',
  },
  {
    field: 'UTM.medium',
    headerName: 'Medio',
    flex: 1,
    minWidth: 130,
    valueGetter: (_, row: ExtendedLead) => row.UTM?.medium || '',
  },
  {
    field: 'visitDate',
    headerName: 'Fecha de visita',
    flex: 1,
    minWidth: 130,
    valueFormatter: (value: Date | null) => formatDateValue(value),
    type: 'date',
  },
  {
    field: 'CRMUpdateAt',
    headerName: 'Subido CRM',
    flex: 1,
    minWidth: 130,
    valueFormatter: (value: Date | null) => formatDateValue(value),
    type: 'date',
  },
  {
    field: 'status',
    headerName: 'Estado',
    flex: 1,
    minWidth: 130,
    valueGetter: (_, row: ExtendedLead) => translateLeadStatus(row.status || ''),
    // Custom filter properties to avoid hardcoding in FilterModal
    filterOptions: [
      'CREATED',
      'HANDLED_BY_AI',
      'INTERESTING_FOR_SALESMAN',
      'ASSIGNED_TO_SALESMAN',
      'PENDING_TO_BE_HANDLED',
      'PENDING_TO_BE_ASSIGNED',
      'HANDLED_BY_SALESMAN',
      'CLOSED',
    ],
    supportsSingleValueOperators: true, // Supports both 'in' and 'equals' operators
  },
  {
    field: 'createdAt',
    headerName: 'Creación',
    flex: 1,
    minWidth: 130,
    type: 'date',
    valueFormatter: (value: Date) => formatDateValue(value),
  },
  {
    field: 'messagesCount',
    headerName: 'Mensajes',
    flex: 1,
    minWidth: 100,
  },
  {
    field: 'discarded',
    headerName: 'Descartado',
    flex: 1,
    minWidth: 110,
    type: 'boolean',
    valueFormatter: (value: boolean) => (value ? 'Si' : 'No'),
  },
  {
    field: 'purchased',
    headerName: '¿Compró?',
    flex: 1,
    minWidth: 120,
    valueFormatter: (value: boolean) => (value ? 'Sí' : 'No'),
    filterable: false,
    sortable: false,
  },
  // Hidden columns for filtering/sorting
  {
    field: 'clientUserId',
    headerName: 'Id del vendedor',
    width: 0,
    valueGetter: (_, row: ExtendedLead) => row.client?.userId || 0,
    filterable: true,
    sortable: false,
  },
  {
    field: 'clientMessagesCount',
    headerName: 'Mensajes cliente',
    width: 0,
    valueGetter: (_, row: ExtendedLead) => row.client?.clientMessagesCount || 0,
    filterable: true,
    sortable: true,
    colSpan: 0,
  },
  {
    field: 'failedMessagesCount',
    headerName: 'Mensajes fallidos',
    width: 0,
    filterable: true,
    sortable: true,
    colSpan: 0,
  },
  {
    field: 'templatesSentCount',
    headerName: 'Plantillas enviadas',
    width: 0,
    valueGetter: (_, row: ExtendedLead) => row.templatesSentCount || 0,
    filterable: true,
    sortable: true,
    colSpan: 0,
  },
  {
    field: 'isTestLead',
    headerName: 'Lead de prueba',
    flex: 1,
    minWidth: 130,
    valueFormatter: (value: boolean) => (value ? 'Sí' : 'No'),
    type: 'boolean',
    filterable: true,
    sortable: true,
  },
  {
    field: 'clientDisabledAutomaticResponses',
    headerName: 'Respuestas Automáticas Deshabilitadas',
    flex: 1,
    minWidth: 360,
    valueGetter: (_, row: ExtendedLead) => row.client?.disabledAutomaticResponses ?? false,
    valueFormatter: (value: boolean) => (value ? 'Sí' : 'No'),
    type: 'boolean',
    filterable: true,
    sortable: true,
  },
  {
    field: 'clientDisabledFollowUps',
    headerName: 'Follow Ups/Campañas Deshabilitadas',
    flex: 1,
    minWidth: 340,
    valueGetter: (_, row: ExtendedLead) => row.client?.disabledFollowUps ?? false,
    valueFormatter: (value: boolean) => (value ? 'Sí' : 'No'),
    type: 'boolean',
    filterable: true,
    sortable: true,
  },
];

/**
 * Car dealership specific columns
 */
export const createCarDealershipColumns = (
  initialBrandOfInterestValues: string[],
  includeCreditColumns = false
): ExtendedGridColDef[] => {
  const baseColumns: ExtendedGridColDef[] = [
    createSingleSelectOrStringColumn(
      'modelsOfInterest[0].brand', // field
      'Marca interés', // headerName
      1, // flex
      140, // minWidth
      initialBrandOfInterestValues, // options
      undefined, // renderCell
      (_, row: ExtendedLead) => row.modelsOfInterest?.[0]?.brand || row.initialBrandOfInterest || '' // valueGetter
    ),
    {
      field: 'modelsOfInterest[0].model',
      headerName: 'Modelo interés',
      flex: 1,
      minWidth: 140,
      valueGetter: (_, row: ExtendedLead) => row.modelsOfInterest?.[0]?.model || row.initialModelOfInterest || '',
    },
    {
      field: 'dealership',
      headerName: 'Concesionario',
      flex: 1,
      minWidth: 140,
      filterable: false,
      sortable: false,
    },
    {
      field: 'branchOffice',
      headerName: 'Sucursal',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'externalLeadOwner',
      headerName: 'Propietario externo',
      flex: 1,
      minWidth: 160,
      filterable: false,
      sortable: false,
    },
    {
      field: 'endReason',
      headerName: 'Motivo de término',
      flex: 1.5,
      minWidth: 180,
      valueGetter: (_: unknown, row: ExtendedLead) => row.endReason || '',
      filterable: true,
      sortable: true,
    },
  ];

  // Add credit fields if requested
  if (includeCreditColumns) {
    baseColumns.push(
      {
        field: 'creditStatus',
        headerName: 'Estado Crédito',
        flex: 1,
        minWidth: 130,
        valueGetter: (_, row: ExtendedLead): string => (row.client?.creditStatus as string) || '',
        sortable: true,
        filterable: true,
      },
      {
        field: 'creditSimulationRequestId',
        headerName: 'Número de solicitud en Santander',
        flex: 1,
        minWidth: 120,
        valueGetter: (_, row: ExtendedLead): string => {
          const simulations = row.client?.creditSimulations;
          if (!simulations || !Array.isArray(simulations) || simulations.length === 0) {
            return '';
          }

          const lastElement = simulations[simulations.length - 1];

          // Handle nested array structure: [[{requestId: "..."}], ...]
          if (Array.isArray(lastElement) && lastElement.length > 0) {
            const lastSimulation = lastElement[lastElement.length - 1] as { requestId?: string } | undefined;
            return lastSimulation?.requestId || '';
          }

          // Handle flat array structure: [{requestId: "..."}, ...]
          if (lastElement && typeof lastElement === 'object' && 'requestId' in lastElement) {
            return (lastElement as { requestId?: string }).requestId || '';
          }

          return '';
        },
      }
    );
  }

  return baseColumns;
};

/**
 * Financial advisor specific columns
 */
export const createFinancialInstitutionColumns = (): ExtendedGridColDef[] => [
  {
    field: 'client.customAttributes.isInDicom',
    headerName: 'En Dicom',
    flex: 1,
    minWidth: 120,
    valueGetter: (_, row: ExtendedLead) => (row.client?.customAttributes?.isInDicom ? 'Sí' : 'No'),
  },
  {
    field: 'client.customAttributes.income',
    headerName: 'Ingresos',
    flex: 1,
    minWidth: 120,
    valueGetter: (_, row: ExtendedLead) =>
      row.client?.customAttributes?.income ? `$${row.client.customAttributes.income.toLocaleString('es-CL')}` : '',
  },
  {
    field: 'client.customAttributes.debtsMonthlyPayment',
    headerName: 'Pago mensual deudas',
    flex: 1,
    minWidth: 160,
    valueGetter: (_, row: ExtendedLead) =>
      row.client?.customAttributes?.debtsMonthlyPayment
        ? `$${row.client.customAttributes.debtsMonthlyPayment.toLocaleString('es-CL')}`
        : '',
  },
  {
    field: 'client.customAttributes.financialLoadRange',
    headerName: 'Carga financiera',
    flex: 1,
    minWidth: 140,
    valueGetter: (_, row: ExtendedLead) => {
      const range = row.client?.customAttributes?.financialLoadRange;
      if (!range) return '';
      const translations: Record<string, string> = {
        low: 'Baja',
        medium: 'Media',
        high: 'Alta',
      };
      return translations[range] || range;
    },
  },
  {
    field: 'creditStatus',
    headerName: 'Estado Crédito',
    flex: 1,
    minWidth: 130,
    valueGetter: (_, row: ExtendedLead): string => (row.client?.creditStatus as string) || '',
    sortable: true,
    filterable: true,
  },
  {
    field: 'creditSimulations',
    headerName: 'Simulaciones',
    flex: 1,
    minWidth: 120,
    valueGetter: (_, row: ExtendedLead): string => {
      const simulations = row.client?.creditSimulations;
      return simulations && Array.isArray(simulations) ? `${simulations.length}` : '0';
    },
  },
];

/**
 * Insurance broker specific columns
 * Note: productOfInterest removed as it's not necessary for insurance brokers
 */
export const createInsuranceBrokerColumns = (includeEndReason = false): ExtendedGridColDef[] =>
  includeEndReason
    ? [
        {
          field: 'endReason',
          headerName: 'Motivo de término',
          flex: 1.5,
          minWidth: 180,
          valueGetter: (_: unknown, row: ExtendedLead) => row.endReason || '',
          filterable: true,
          sortable: true,
        },
      ]
    : [];

const UTM_COLUMN_CONFIG = [
  { field: 'utmSource', headerName: 'UTM Source', minWidth: 120 },
  { field: 'utmMedium', headerName: 'UTM Medium', minWidth: 120 },
  { field: 'utmCampaign', headerName: 'UTM Campaign', minWidth: 140 },
  { field: 'utmId', headerName: 'UTM ID', minWidth: 100 },
  { field: 'utmTerm', headerName: 'UTM Term', minWidth: 120 },
  { field: 'utmContent', headerName: 'UTM Content', minWidth: 120 },
  { field: 'utmUrlOrigin', headerName: 'UTM URL Origin', minWidth: 140 },
] as const;

/**
 * Mueve seguro UTM columns from webhook
 */
export const createMueveSeguroUTMColumns = (): ExtendedGridColDef[] =>
  UTM_COLUMN_CONFIG.map(({ field, headerName, minWidth }) => ({
    field: `utmFromWebhook.${field}`,
    headerName,
    flex: 1,
    minWidth,
    valueGetter: (_, row: ExtendedLead) => row.utmFromWebhook?.[field] || '',
    filterable: true,
    sortable: true,
  }));

/**
 * Default columns when no specific business unit type is set
 */
export const createDefaultColumns = (initialBrandOfInterestValues: string[]): ExtendedGridColDef[] => [
  createSingleSelectOrStringColumn(
    'modelsOfInterest[0].brand', // field
    'Marca interés', // headerName
    1, // flex
    140, // minWidth
    initialBrandOfInterestValues, // options
    undefined, // renderCell
    (_, row: ExtendedLead) => row.modelsOfInterest?.[0]?.brand || row.initialBrandOfInterest || '' // valueGetter
  ),
  {
    field: 'modelsOfInterest[0].model',
    headerName: 'Modelo interés',
    flex: 1,
    minWidth: 140,
    valueGetter: (_, row: ExtendedLead) => row.modelsOfInterest?.[0]?.model || row.initialModelOfInterest || '',
  },
];

/**
 * Main function to get all columns based on business unit type
 */
export const getLeadTableColumns = (
  sourceValues: string[],
  initialBrandOfInterestValues: string[],
  campaignValues: string[],
  businessUnitType: 'car_dealership' | 'financial_institution' | 'insurance_broker' | 'after_sales' | null = null,
  businessUnitId?: number | null,
  topicsConfig?: TopicConfigList
): ExtendedGridColDef[] => {
  const AUTOLAND_VENTAS_BUSINESS_UNIT_ID = 45;
  const includeCreditColumns = businessUnitId === AUTOLAND_VENTAS_BUSINESS_UNIT_ID;
  const includeMueveSeguroUTM = businessUnitId === INSUREX_BUSINESS_UNIT_ID;

  const commonColumns = createCommonColumns(sourceValues, campaignValues, topicsConfig);

  let specificColumns: ExtendedGridColDef[] = [];

  switch (businessUnitType) {
    case 'car_dealership':
      specificColumns = createCarDealershipColumns(initialBrandOfInterestValues, includeCreditColumns);
      break;
    case 'financial_institution':
      specificColumns = createFinancialInstitutionColumns();
      break;
    case 'insurance_broker':
      specificColumns = createInsuranceBrokerColumns(includeMueveSeguroUTM);
      break;
    default:
      specificColumns = createDefaultColumns(initialBrandOfInterestValues);
      break;
  }

  // Add UTM columns for mueve seguro leads
  const utmColumns = includeMueveSeguroUTM ? createMueveSeguroUTMColumns() : [];

  // Add AI Comment column for Kovacs leads
  const includeKovacsAIComment = businessUnitId === KOVACS_BUSINESS_UNIT_ID;
  const kovacsAICommentColumn: ExtendedGridColDef[] = includeKovacsAIComment
    ? [
        {
          field: 'AIComment',
          headerName: 'Comentario IA',
          flex: 1.5,
          minWidth: 200,
          valueGetter: (_, row: ExtendedLead) => row.AIComment || '',
          filterable: true,
          sortable: false,
        },
      ]
    : [];

  // Bravoauto leads carry the plate of the car the lead came in for inside their creation payload
  const patenteColumn: ExtendedGridColDef[] =
    businessUnitId === BRAVOAUTO_BUSINESS_UNIT_ID
      ? [
          {
            field: 'patente',
            headerName: 'Patente',
            width: 110,
            valueGetter: (_, row: ExtendedLead) => getPatenteFromWebhookBody(row),
            // Derived client-side from a JSON payload, so the server cannot filter or sort by it
            filterable: false,
            sortable: false,
          },
        ]
      : [];

  return [...commonColumns, ...kovacsAICommentColumn, ...patenteColumn, ...specificColumns, ...utmColumns];
};
